// Email send service, calls the send-email Supabase Edge Function
// which in turn calls Resend. API key lives in Supabase secrets only.

/**
 * Send an email campaign or newsletter via Resend.
 *
 * Flow:
 *   1. Query email_suppressions, filter out bounced/complained/unsubscribed
 *   2. Insert email_sends row (status: 'sending') to get the send ID
 *   3. Call send-email edge function with sendId so Resend tags each email
 *   4. Resend webhook picks up opens/clicks and increments counters on that row
 *   5. Update email_sends to 'sent' or 'partial'
 *
 * @param {object} opts
 * @param {string} opts.subject
 * @param {string} opts.fromName
 * @param {string} opts.fromEmail   - Must be from a Resend-verified domain
 * @param {string} opts.html        - Full email HTML
 * @param {{email:string, name?:string}[]} opts.recipients
 * @param {'campaign'|'newsletter'} [opts.type]
 * @param {string|null} [opts.managedAccountId]
 * @returns {Promise<{sent:number, suppressed:number, errors:unknown[], sendId:string|null}>}
 */
export async function sendEmail({
  subject, fromName, fromEmail, html, recipients, type = 'campaign', managedAccountId = null
}) {
  const { supabase } = await import('../lib/supabaseClient');

  // ── 1. Suppression filter ────────────────────────────────────────────────
  let cleanRecipients = recipients;
  let suppressed = 0;
  try {
    const emails = recipients.map(r => r.email.toLowerCase().trim());
    const { data: suppressedRows } = await supabase
      .from('email_suppressions')
      .select('email')
      .in('email', emails);

    if (suppressedRows?.length) {
      const suppressedSet = new Set(suppressedRows.map(r => r.email.toLowerCase()));
      const before = recipients.length;
      cleanRecipients = recipients.filter(r => !suppressedSet.has(r.email.toLowerCase().trim()));
      suppressed = before - cleanRecipients.length;
      if (suppressed > 0) {
        console.info(`[emailSendService] Suppressed ${suppressed} recipient(s) before send.`);
      }
    }
  } catch (supErr) {
    console.warn('[emailSendService] Suppression check failed (sending anyway):', supErr);
  }

  if (!cleanRecipients.length) {
    return { sent: 0, suppressed, errors: ['All recipients were suppressed'], sendId: null };
  }

  // ── 2. Insert email_sends row first to get the ID ────────────────────────
  let sendId = null;
  try {
    const { data: sendRow, error: insertErr } = await supabase
      .from('email_sends')
      .insert({
        type,
        subject,
        from_name:          fromName,
        from_email:         fromEmail,
        recipient_count:    cleanRecipients.length,
        status:             'sending',
        html_body:          html,
        managed_account_id: managedAccountId || null,
      })
      .select('id')
      .single();

    if (!insertErr && sendRow?.id) sendId = sendRow.id;
  } catch (insertErr) {
    console.warn('[emailSendService] Failed to pre-create email_sends row:', insertErr);
  }

  // ── 3. Send via edge function (passes sendId as Resend tag) ──────────────
  const { data, error } = await supabase.functions.invoke('send-email', {
    body: { subject, fromName, fromEmail, html, recipients: cleanRecipients, type, sendId },
  });

  if (error) {
    if (sendId) {
      await supabase.from('email_sends').update({ status: 'failed' }).eq('id', sendId);
    }
    throw new Error(error.message || 'Edge Function error');
  }
  if (!data?.success) {
    if (sendId) {
      await supabase.from('email_sends').update({ status: 'failed' }).eq('id', sendId);
    }
    throw new Error(data?.error || 'Send failed');
  }

  // ── 4. Update email_sends with final status + actual sent count ──────────
  if (sendId) {
    try {
      await supabase.from('email_sends').update({
        status:          data.errors?.length > 0 ? 'partial' : 'sent',
        recipient_count: data.sent,
        sent_at:         new Date().toISOString(),
      }).eq('id', sendId);
    } catch (updateErr) {
      console.warn('[emailSendService] Failed to update email_sends status:', updateErr);
    }
  }

  return { sent: data.sent, suppressed, errors: data.errors ?? [], sendId };
}

/**
 * Fetch active newsletter subscribers from Supabase.
 * Returns [] gracefully if the table doesn't exist yet.
 */
export async function fetchNewsletterSubscribers() {
  const { supabase } = await import('../lib/supabaseClient');
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select('id, email, first_name, last_name, status')
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) {
    console.warn('[emailSendService] newsletter_subscribers not available:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Subscribe an email to the newsletter.
 */
export async function subscribeToNewsletter({ email, firstName, lastName, source = 'website' }) {
  const { supabase } = await import('../lib/supabaseClient');
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .upsert({ email, first_name: firstName, last_name: lastName, source, status: 'active' }, { onConflict: 'email' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Unsubscribe by email (called from the unsubscribe landing page).
 */
export async function unsubscribe(email) {
  const { supabase } = await import('../lib/supabaseClient');
  const { error } = await supabase
    .from('newsletter_subscribers')
    .update({ status: 'unsubscribed', unsubscribed_at: new Date().toISOString() })
    .eq('email', email);
  if (error) throw error;
}
